import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Modal } from "react-bootstrap";
import moment from "moment";
import { FiCalendar } from "react-icons/fi";


import { ButtonView, Input, Pagination } from "../../../controls";

const MyOccupanciesTab = ({
  user,
  occupancyHistoryList,
  cancelBooking,
  checkOutRoom,
  getCurrentOccupancy,
  getOccupancyHistory,
  myOccupancyPage,
  setMyOccupancyPage,
  myOccupancySelectedDate,
  setMyOccupancyFilters,
}) => {
  const [selectedDate, setSelectedDate] = useState(myOccupancySelectedDate ? new Date(myOccupancySelectedDate) : new Date());
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOccupancy, setSelectedOccupancy] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [occupancyToCheckout, setOccupancyToCheckout] = useState(null);

  const [currentPage, setCurrentPage] = useState(myOccupancyPage || 1);
  const itemsPerPage = 10;

  React.useEffect(() => {
    if (myOccupancyPage) {
      setCurrentPage(myOccupancyPage);
    }
  }, [myOccupancyPage]);

  const isFirstMount = React.useRef(true);

  React.useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setCurrentPage(1);
    if (setMyOccupancyPage) {
      setMyOccupancyPage(1);
    }
  }, [selectedDate, setMyOccupancyPage]);

  React.useEffect(() => {
    const payload = { limit: 500 };
    if (selectedDate) {
      payload.fromDate = moment(selectedDate).startOf("day").toISOString();
      payload.toDate = moment(selectedDate).endOf("day").toISOString();
    }
    getOccupancyHistory(payload);
  }, [getOccupancyHistory, selectedDate]);

  const handleRefresh = () => {
    getCurrentOccupancy();
    const payload = { limit: 500 };
    if (selectedDate) {
      payload.fromDate = moment(selectedDate).startOf("day").toISOString();
      payload.toDate = moment(selectedDate).endOf("day").toISOString();
    }
    getOccupancyHistory(payload);
  };

  const handleCancelClick = (occ) => {
    setSelectedOccupancy(occ);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    if (selectedOccupancy) {
      cancelBooking({ occupancyId: selectedOccupancy._id });
      setShowCancelModal(false);
      setSelectedOccupancy(null);
      setTimeout(handleRefresh, 800);
    }
  };

  const handleEarlyCheckOut = (occId) => {
    setOccupancyToCheckout(occId);
    setShowCheckoutModal(true);
  };

  const handleConfirmCheckout = () => {
    if (occupancyToCheckout) {
      checkOutRoom({
        occupancyId: occupancyToCheckout,
        checkOutTime: new Date().toISOString(),
      });
      setShowCheckoutModal(false);
      setOccupancyToCheckout(null);
      setTimeout(handleRefresh, 800);
    }
  };


  const myOccupancies = (occupancyHistoryList || [])
    .filter((occ) => occ.employeeId && user && occ.employeeId._id === user._id)
    .filter((occ) => {
      if (!selectedDate) return true;
      return moment(occ.checkInTime).format("YYYY-MM-DD") === moment(selectedDate).format("YYYY-MM-DD");
    })
    .sort((a, b) => moment(b.checkInTime).diff(moment(a.checkInTime)));

  return (
    <div className="my-occupancies-tab mt-3">
      <div className="d-flex justify-content-end align-items-end mb-5">
        <div style={{ width: "200px" }}>
          <Input
            type="date"
            name="selectedDate"
            label="Date"
            value={selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : ""}
            onChange={(e) => {
              const d = e.target.value ? new Date(e.target.value) : null;
              setSelectedDate(d);
              if (setMyOccupancyFilters) {
                setMyOccupancyFilters(d ? d.toISOString() : null);
              }
            }}
          />
        </div>
      </div>

      {myOccupancies.length === 0 ? (
        <div className="text-center p-5">
          <div className="text-muted mb-2">
            <FiCalendar size={40} />
          </div>
          <h5>No Bookings Found</h5>
          <p className="text-muted m-0">You have no bookings recorded for the selected dates.</p>
        </div>
      ) : (
        <>
          <Row>
            {myOccupancies.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((occ) => {
            const checkOutMoment = occ.checkOutTime 
              ? moment(occ.checkOutTime) 
              : moment(occ.checkInTime).set({ hour: 21, minute: 0, second: 0 });
            
            const isCompleted = occ.status ? occ.status.toUpperCase() === 'COMPLETED' : checkOutMoment.isBefore(moment());
            const isFuture = occ.status ? occ.status.toUpperCase() === 'UPCOMING' : moment(occ.checkInTime).isAfter(moment());
            const isActive = occ.status ? occ.status.toUpperCase() === 'ACTIVE' : (moment(occ.checkInTime).isBefore(moment()) && checkOutMoment.isAfter(moment()));
            const isCancelled = occ.status ? occ.status.toUpperCase() === 'CANCELLED' : occ.isCancelled;

            let badgeVariant = "success-badge";
            let badgeText = "Active";

            if (isCancelled) {
              badgeVariant = "secondary-badge";
              badgeText = "Cancelled";
            } else if (isCompleted) {
              badgeVariant = "completed-badge";
              badgeText = "Completed";
            } else if (isFuture) {
              badgeVariant = "warning-badge";
              badgeText = "Upcoming";
            }

            return (
              <Col md={4} key={occ._id} className="mb-3">
                <Card className="occupancy-card h-100">
                  <Card.Body className="d-flex flex-column justify-content-between p-3">
                    <div>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="room-title mb-1">{occ.roomId ? occ.roomId.name : "N/A"}</h5>
                        <span className={`status-dot-badge ${badgeVariant}`}>
                          <span className="dot"></span>
                          {badgeText}
                        </span>
                      </div>
                      <div className="room-meta mb-2">
                        <strong>Location:</strong> {occ.roomId && occ.roomId.location ? (occ.roomId.location.name || occ.roomId.location) : "N/A"}
                      </div>
                      <div className="details-text mb-2">
                        <strong>Purpose:</strong> {occ.purpose || "-"}
                      </div>
                      <div className="details-text mb-2">
                        <strong>Date:</strong> {moment(occ.checkInTime).format("DD-MM-YYYY")}
                      </div>
                      <div className="details-text mb-2">
                        <strong>Time:</strong> {moment(occ.checkInTime).format("h:mm a")} - {occ.checkOutTime ? moment(occ.checkOutTime).format("h:mm a") : "9:00 PM"}
                      </div>
                      <div className="details-text mb-2">
                        <strong>Type:</strong> {occ.occupancyType}
                      </div>
                    </div>

                    <div className="mt-3 d-flex justify-content-end align-items-center">
                      {isFuture && !isCancelled && (
                        <ButtonView
                          onClick={() => handleCancelClick(occ)}
                          title="Cancel Booking"
                          styles="primal"
                          size="small"
                        />
                      )}
                      {isActive && !isCancelled && (
                        <ButtonView
                          onClick={() => handleEarlyCheckOut(occ._id)}
                          title="Checkout Early"
                          styles="secondary"
                          size="small"
                        />
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
          </Row>
          
          {myOccupancies.length > 0 ? (
            <div className="d-flex justify-content-end mt-4 mb-2">
              <Pagination
                rowsPerPage={itemsPerPage}
                rowCount={myOccupancies.length}
                onChangePage={(page) => {
                  setCurrentPage(page);
                  if (setMyOccupancyPage) {
                    setMyOccupancyPage(page);
                  }
                }}
                currentPage={currentPage}
              />
            </div>
          ) : null}
        </>
      )}

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} animation={false} centered>
        <Modal.Header>
          <Modal.Title>Cancel Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel your booking for{" "}
          <strong>{selectedOccupancy && selectedOccupancy.roomId ? selectedOccupancy.roomId.name : ""}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); setShowCancelModal(false); }}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); handleConfirmCancel(); }}
          >
            Confirm Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCheckoutModal} onHide={() => setShowCheckoutModal(false)} animation={false} centered>
        <Modal.Header>
          <Modal.Title>Check-out Early</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to check out of this room early?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); setShowCheckoutModal(false); }}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); handleConfirmCheckout(); }}
          >
            Confirm Check-out
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

MyOccupanciesTab.propTypes = {
  user: PropTypes.object.isRequired,
  occupancyHistoryList: PropTypes.array.isRequired,
  cancelBooking: PropTypes.func.isRequired,
  checkOutRoom: PropTypes.func.isRequired,
  getCurrentOccupancy: PropTypes.func.isRequired,
  getOccupancyHistory: PropTypes.func.isRequired,
  myOccupancyPage: PropTypes.number,
  setMyOccupancyPage: PropTypes.func,
  myOccupancySelectedDate: PropTypes.string,
  setMyOccupancyFilters: PropTypes.func,
};

export default MyOccupanciesTab;

